import type { PageBlueprint } from "../types";

/**
 * 1. Faculty Standard Blueprint
 * Traditional academic blueprint tailored for faculty profiles.
 */
export const facultyStandardBlueprint: PageBlueprint = {
  id: "blueprint-faculty-standard",
  name: "Faculty Standard Profile",
  category: "faculty-teaching",
  adaptations: {
    starter: {
      description: "Art-directed single-page narrative featuring selected publications, teaching, and bio.",
      pages: [
        {
          slug: "",
          title: "Home",
          navigationLabel: "Home",
          isHome: true,
          requiredSections: ["about", "publications", "contact"],
          optionalSections: ["teaching", "experience", "awards"],
          defaultSectionOrder: [
            "about",
            "publications",
            "teaching",
            "experience",
            "awards",
            "contact",
          ],
          contentEmphasis: ["publications", "teaching"],
        },
      ],
      maxPublicationsSummary: 5,
    },
    pro: {
      description: "3-page portfolio with dedicated Research & Publications and Teaching & CV subpages.",
      pages: [
        {
          slug: "",
          title: "Home",
          navigationLabel: "Home",
          isHome: true,
          requiredSections: ["about", "contact"],
          optionalSections: ["awards"],
          defaultSectionOrder: ["about", "awards", "contact"],
          contentEmphasis: ["experience"],
        },
        {
          slug: "research",
          title: "Research & Publications",
          navigationLabel: "Research",
          isHome: false,
          requiredSections: ["publications"],
          optionalSections: ["projects"],
          defaultSectionOrder: ["publications", "projects"],
          contentEmphasis: ["publications", "projects"],
        },
        {
          slug: "teaching",
          title: "Teaching & CV",
          navigationLabel: "Teaching & CV",
          isHome: false,
          requiredSections: ["teaching", "experience"],
          optionalSections: ["awards"],
          defaultSectionOrder: ["teaching", "experience", "awards"],
          contentEmphasis: ["teaching", "experience"],
        },
      ],
    },
    premium: {
      description: "Comprehensive 4-page site with separate Research, Publications, and CV dossiers.",
      pages: [
        {
          slug: "",
          title: "Home",
          navigationLabel: "Home",
          isHome: true,
          requiredSections: ["about", "contact"],
          optionalSections: [],
          defaultSectionOrder: ["about", "contact"],
          contentEmphasis: [],
        },
        {
          slug: "research",
          title: "Research Agenda",
          navigationLabel: "Research",
          isHome: false,
          requiredSections: ["projects"],
          optionalSections: [],
          defaultSectionOrder: ["projects"],
          contentEmphasis: ["projects", "grants"],
        },
        {
          slug: "publications",
          title: "Publications",
          navigationLabel: "Publications",
          isHome: false,
          requiredSections: ["publications"],
          optionalSections: [],
          defaultSectionOrder: ["publications"],
          contentEmphasis: ["publications"],
        },
        {
          slug: "cv",
          title: "Curriculum Vitae",
          navigationLabel: "CV",
          isHome: false,
          requiredSections: ["experience", "teaching", "awards"],
          optionalSections: [],
          defaultSectionOrder: ["experience", "teaching", "awards"],
          contentEmphasis: ["experience", "teaching"],
        },
      ],
    },
  },
};

/**
 * 2. Research Showcase Blueprint
 * Emphasizes research grants, software projects, and peer-reviewed papers.
 */
export const researchShowcaseBlueprint: PageBlueprint = {
  id: "blueprint-research-showcase",
  name: "Research Showcase Blueprint",
  category: "research-publications",
  adaptations: {
    starter: {
      description: "Single-page research overview combining project highlights and top citations.",
      pages: [
        {
          slug: "",
          title: "Home",
          navigationLabel: "Home",
          isHome: true,
          requiredSections: ["about", "publications", "projects", "contact"],
          optionalSections: ["skills", "experience"],
          defaultSectionOrder: [
            "about",
            "projects",
            "publications",
            "skills",
            "experience",
            "contact",
          ],
          contentEmphasis: ["projects", "publications"],
        },
      ],
      maxPublicationsSummary: 6,
    },
    pro: {
      description: "Dedicated Research Projects and Publications bibliography pages.",
      pages: [
        {
          slug: "",
          title: "Home",
          navigationLabel: "Home",
          isHome: true,
          requiredSections: ["about", "contact"],
          optionalSections: ["skills"],
          defaultSectionOrder: ["about", "skills", "contact"],
          contentEmphasis: ["skills"],
        },
        {
          slug: "projects",
          title: "Funded Projects & Labs",
          navigationLabel: "Projects",
          isHome: false,
          requiredSections: ["projects"],
          optionalSections: [],
          defaultSectionOrder: ["projects"],
          contentEmphasis: ["projects"],
        },
        {
          slug: "publications",
          title: "Bibliography",
          navigationLabel: "Publications",
          isHome: false,
          requiredSections: ["publications"],
          optionalSections: [],
          defaultSectionOrder: ["publications"],
          contentEmphasis: ["publications"],
        },
      ],
    },
    premium: {
      description: "Full research group presence with dedicated Lab Team and Research Output pages.",
      pages: [
        {
          slug: "",
          title: "Home",
          navigationLabel: "Home",
          isHome: true,
          requiredSections: ["about", "contact"],
          optionalSections: [],
          defaultSectionOrder: ["about", "contact"],
          contentEmphasis: [],
        },
        {
          slug: "projects",
          title: "Research Programs",
          navigationLabel: "Projects",
          isHome: false,
          requiredSections: ["projects"],
          optionalSections: [],
          defaultSectionOrder: ["projects"],
          contentEmphasis: ["projects"],
        },
        {
          slug: "publications",
          title: "Papers & Preprints",
          navigationLabel: "Publications",
          isHome: false,
          requiredSections: ["publications"],
          optionalSections: [],
          defaultSectionOrder: ["publications"],
          contentEmphasis: ["publications"],
        },
        {
          slug: "team",
          title: "Lab Roster & Alumni",
          navigationLabel: "Team",
          isHome: false,
          requiredSections: ["experience", "teaching"],
          optionalSections: [],
          defaultSectionOrder: ["experience", "teaching"],
          contentEmphasis: ["experience"],
        },
      ],
    },
  },
};

/**
 * 3. Early Career & PhD Blueprint
 * Designed for PhD candidates, postdocs, and early-career job market scholars.
 */
export const earlyCareerBlueprint: PageBlueprint = {
  id: "blueprint-early-career",
  name: "Early-Career & PhD Blueprint",
  category: "early-career-phd",
  adaptations: {
    starter: {
      description: "Polished single-page academic dossier highlighting dissertation, skills, and papers.",
      pages: [
        {
          slug: "",
          title: "Home",
          navigationLabel: "Home",
          isHome: true,
          requiredSections: ["about", "publications", "contact"],
          optionalSections: ["projects", "experience", "skills"],
          defaultSectionOrder: [
            "about",
            "publications",
            "projects",
            "skills",
            "experience",
            "contact",
          ],
          contentEmphasis: ["publications", "skills"],
        },
      ],
      maxPublicationsSummary: 4,
    },
    pro: {
      description: "2-page site with separate Academic CV & Talks page.",
      pages: [
        {
          slug: "",
          title: "Home",
          navigationLabel: "Home",
          isHome: true,
          requiredSections: ["about", "publications", "contact"],
          optionalSections: ["skills"],
          defaultSectionOrder: ["about", "publications", "skills", "contact"],
          contentEmphasis: ["publications"],
        },
        {
          slug: "cv",
          title: "Curriculum Vitae & Talks",
          navigationLabel: "CV & Talks",
          isHome: false,
          requiredSections: ["experience", "projects"],
          optionalSections: ["awards"],
          defaultSectionOrder: ["experience", "projects", "awards"],
          contentEmphasis: ["experience"],
        },
      ],
    },
    premium: {
      description: "3-page early-career hub including Teaching Portfolio & Research Statement.",
      pages: [
        {
          slug: "",
          title: "Home",
          navigationLabel: "Home",
          isHome: true,
          requiredSections: ["about", "contact"],
          optionalSections: [],
          defaultSectionOrder: ["about", "contact"],
          contentEmphasis: [],
        },
        {
          slug: "research",
          title: "Dissertation & Papers",
          navigationLabel: "Research",
          isHome: false,
          requiredSections: ["publications", "projects"],
          optionalSections: [],
          defaultSectionOrder: ["publications", "projects"],
          contentEmphasis: ["publications", "projects"],
        },
        {
          slug: "cv",
          title: "Curriculum Vitae",
          navigationLabel: "CV",
          isHome: false,
          requiredSections: ["experience", "teaching", "awards"],
          optionalSections: [],
          defaultSectionOrder: ["experience", "teaching", "awards"],
          contentEmphasis: ["experience", "teaching"],
        },
      ],
    },
  },
};

export const ALL_BLUEPRINTS: PageBlueprint[] = [
  facultyStandardBlueprint,
  researchShowcaseBlueprint,
  earlyCareerBlueprint,
];
